/*----------------------------------------------------------------------------
    EditAddVentLog.tsx

----------------------------------------------------------------------------*/

import React from 'react';
import '../App.css';
import { makeStyles, Text, Divider, Dialog, DialogTrigger, Button, DialogSurface, DialogBody, DialogTitle, DialogContent, Field, Input, DialogActions, Dropdown, Option, SelectionEvents, OptionOnSelectData } from
    '@fluentui/react-components';
import { InputType, InputTypes, RexInput } from '../components/RexInput';
import { FormControls } from "../model/FormControls";
import { IAppContext } from "../model/IAppContext";
import { TheAppContext } from "../model/AppContext";
import { withStyles } from '../components/withStyles';
import { VentLog } from "../model/VentLog";
import { RexDateTime } from '../Dates/RexDateTime';

export interface OnDismissDelegate
{
    (): void;
}

export interface OnSaveDelegate
{
    (newItem: VentLog): void;
}

export interface EditAddVentLogProps
{
    styles: Record<string, string>;
    isAdd: boolean;
    open?: boolean;
    onSave: OnSaveDelegate;
    onDismiss: OnDismissDelegate;
    itemEditing: VentLog;
}

export interface EditAddVentLogState
{
    itemEditing: VentLog;
    isOpen: boolean;
}

const useStyles = makeStyles(
    {
        stackHorizontal:
        {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between'
        },
        stackVertical:
        {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
        }
        //        dividerCustom: {
        //            fontSize: '14px',
        //            fontWeight: 'bold'
        //        },
        //        insetContent: {
        //            marginLeft: '.1in',
        //            marginRight: '.1in'
        //        }

    });

class EditAddVentLogWithoutStyles extends React.Component<EditAddVentLogProps, EditAddVentLogState>
{
    context!: IAppContext;
    static contextType = TheAppContext;
    m_controls: FormControls<VentLog>;

    constructor(props: EditAddVentLogProps)
    {
        super(props);
        this.state =
        {
            isOpen: this.props.open ?? false,
            itemEditing: this.props.itemEditing,
        };

        this.m_controls = new FormControls<VentLog>(
            [
                ["SmokeID", "smokeID"],
                ["Timestamp", "timestamp"],
                ["Vent1", "vent1"],
                ["Vent2", "vent2"],
                ["Vent3", "vent3"],
                ["Comment", "comment"],
            ]);
    }

    async componentDidMount()
    {
        this.m_controls.updateForData(this.state.itemEditing);
    }

    async componentDidUpdate(prevProps: EditAddVentLogProps, prevState: EditAddVentLogState)
    {
        if (prevProps.itemEditing != this.props.itemEditing)
        {
            this.m_controls.updateForData(this.state.itemEditing);
            this.setState(
                {
                    itemEditing: this.props.itemEditing,
                });
        }
    }

    async onCommit(formEvent: React.FormEvent)
    {
        formEvent.preventDefault(); // don't auto dismiss

        // need to query the DOM and populate the item from that to allow
        // autofill (which doesn't fire any events)
        const newItem = this.m_controls.getDataFromFormForEvent(formEvent);

        newItem.SmokeID = this.state.itemEditing.SmokeID;
        newItem.Timestamp = RexDateTime.createFromString(newItem.Timestamp as unknown as string);

        this.props.onSave(newItem);
    }

    getInputType(field: string): InputType
    {
        switch (field)
        {
            case "Timestamp":
                return InputTypes.DateTime;
            case "Vent1":
                return InputTypes.Integer;
            case "Vent2":
                return InputTypes.Integer;
            case "Vent3":
                return InputTypes.Integer;
            default:
                return InputTypes.Standard;
        }
    }

    handleInputChange(field: string, value: string)
    {
        const inputType = this.getInputType(field);

        const valueToUse = RexInput.fieldValueFromInputValue(inputType, value);

        this.setState(
            (prevState) => ({
                itemEditing:
                {
                    ...prevState.itemEditing,
                    [field]: valueToUse
                }
            }));
    }

    dismissDialog()
    {
        this.props.onDismiss();
    }

    render()
    {
        const ventLog = this.state.itemEditing;
        const onChange = this.handleInputChange.bind(this);
        const dismissOpen = () => this.dismissDialog();
        const adding = this.props.isAdd;

        return (
            <Dialog modalType="modal" open={this.props.open}>
                <DialogSurface>
                    <form onSubmit={(event) => this.onCommit(event)}>
                        <DialogBody>
                            <DialogTitle>VentLog</DialogTitle>
                            <DialogContent className={this.props.styles.content}>
                                <RexInput label="Timestamp" controls={this.m_controls} controlName="Timestamp" style={{ width: '16em' }} onChange={onChange} value={ventLog.Timestamp}/>
                                <div className={this.props.styles.stackHorizontal}>
                                    <RexInput label="Vent1" controls={this.m_controls} controlName="Vent1" style={{ width: '5em' }} onChange={onChange} value={ventLog.Vent1}/>
                                    <RexInput label="Vent2" controls={this.m_controls} controlName="Vent2" style={{ width: '5em' }} onChange={onChange} value={ventLog.Vent2}/>
                                    <RexInput label="Vent3" controls={this.m_controls} controlName="Vent3" style={{ width: '5em' }} onChange={onChange} value={ventLog.Vent3}/>
                                </div>
                                <RexInput label="Comment" controls={this.m_controls} controlName="Comment" style={{ width: '32em' }} onChange={onChange} value={ventLog.Comment}/>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={dismissOpen} appearance="secondary">Cancel</Button>
                                <Button type="submit" appearance="primary">Save</Button>
                            </DialogActions>
                        </DialogBody>
                    </form>
                </DialogSurface>
            </Dialog>)
    }
}

export const EditAddVentLog = withStyles(useStyles, EditAddVentLogWithoutStyles);